import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Search, Assignment } from "@mui/icons-material";
import Swal from "sweetalert2";

const UnassignedLoans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loans, setLoans] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [selectedLoans, setSelectedLoans] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedOfficer, setSelectedOfficer] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingOfficers, setLoadingOfficers] = useState(false);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || "1", 10) || 1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalArrears, setTotalArrears] = useState(0);
  const loansPerPage = 20;

  const totalPages = Math.ceil(totalCount / loansPerPage);

  // Format currency helper
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format number helper
  const formatNumber = (value) => {
    if (value === null || value === undefined) return "0";
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Fetch officers
  const fetchOfficers = async () => {
    setLoadingOfficers(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/collections-officers`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      const officersList = data.collectionsOfficers || [];
      // Sort officers by first name alphabetically
      const sortedOfficers = officersList.sort((a, b) => {
        const firstNameA = (a.firstName || "").toLowerCase();
        const firstNameB = (b.firstName || "").toLowerCase();
        return firstNameA.localeCompare(firstNameB);
      });
      setOfficers(sortedOfficers);
    } catch (error) {
      console.error("Error fetching officers:", error);
    } finally {
      setLoadingOfficers(false);
    }
  };

  // Fetch loans
  const fetchLoans = async () => {
    setLoading(true);
    try {
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : "";
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/unassigned-loans?page=${currentPage}&limit=${loansPerPage}${searchParam}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      setLoans(data.data || []);
      setTotalCount(data.pagination?.totalCount || 0);
      setTotalArrears(data.totalArrears || 0);
    } catch (error) {
      console.error("Error fetching loans:", error);
    } finally {
      setLoading(false);
    }
  };

  // Keep URL in sync with filters so back-navigation restores them
  useEffect(() => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (currentPage > 1) params.page = String(currentPage);
    setSearchParams(params, { replace: true });
  }, [searchTerm, currentPage, setSearchParams]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchLoans();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchLoans();
  }, [currentPage]);

  useEffect(() => {
    fetchOfficers();
  }, []);

  // Handle select all
  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedLoans([]);
      setIsAllSelected(false);
    } else {
      const allLoanIds = loans.map((loan) => loan._id);
      setSelectedLoans(allLoanIds);
      setIsAllSelected(true);
    }
  };

  // Handle individual loan selection
  const handleLoanSelection = (loanId) => {
    setSelectedLoans((prev) =>
      prev.includes(loanId)
        ? prev.filter((id) => id !== loanId)
        : [...prev, loanId]
    );
    setIsAllSelected(false);
  };

  // Update isAllSelected when loans change
  useEffect(() => {
    if (loans.length > 0) {
      setIsAllSelected(selectedLoans.length === loans.length && loans.every((loan) => selectedLoans.includes(loan._id)));
    }
  }, [selectedLoans, loans]);

  // Handle assign
  const handleAssign = async () => {
    if (selectedLoans.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Loans Selected",
        text: "Please select at least one loan to assign.",
      });
      return;
    }
    if (!selectedOfficer) {
      Swal.fire({
        icon: "warning",
        title: "No Officer Selected",
        text: "Please select an officer to assign loans to.",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/assign-loans`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            officerId: selectedOfficer,
            loanIds: selectedLoans,
          }),
        }
      );
      const result = await response.json();
      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: result.message || `${selectedLoans.length} loan(s) assigned successfully.`,
        });
        setSelectedLoans([]);
        setIsAllSelected(false);
        setSelectedOfficer("");
        fetchLoans();
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: result.message || "Failed to assign loans.",
        });
      }
    } catch (error) {
      console.error("Error assigning loans:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error assigning loans. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLoanClick = (loanId) => {
    const returnPath = `/unassigned-loans${location.search || ''}`;
    navigate(`/loan/${loanId}`, {
      state: {
        from: 'Unassigned Loans',
        returnPath
      }
    });
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Assignment className="text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Unassigned Loans</h1>
          </div>
          <p className="text-gray-600">Loans that have not been assigned to a collections officer</p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by loan number or client name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Assignment Section */}
        {selectedLoans.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="font-bold text-lg mb-4 text-gray-700">Assign To</h3>
            
            {loadingOfficers ? (
              <div className="text-gray-500 text-sm">Loading Officers...</div>
            ) : officers.length === 0 ? (
              <div className="text-red-500 text-sm">No officers found. Please ensure agents are assigned to you in the admin portal.</div>
            ) : (
              <div className="flex flex-wrap gap-2 mb-4">
                {officers.map((officer) => {
                  const displayName = officer.firstName;
                  const isSelected = selectedOfficer === officer._id;
                  
                  return (
                    <button
                      key={officer._id}
                      onClick={() => setSelectedOfficer(isSelected ? "" : officer._id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        isSelected
                          ? "bg-blue-500 text-white hover:bg-blue-600"
                          : "bg-blue-100 text-gray-700 hover:bg-blue-200"
                      }`}
                    >
                      {displayName}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={handleAssign}
                className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                disabled={selectedLoans.length === 0 || !selectedOfficer || loading}
              >
                {loading ? "Assigning..." : `Assign ${selectedLoans.length} ${selectedLoans.length === 1 ? "Loan" : "Loans"}`}
              </button>
              
              <button
                onClick={() => {
                  setSelectedLoans([]);
                  setIsAllSelected(false);
                  setSelectedOfficer("");
                }}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Clear Selection
              </button>

              {selectedLoans.length > 0 && !selectedOfficer && (
                <p className="text-sm text-red-600">
                  Please select an officer
                </p>
              )}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600 mb-1">Unassigned Loans</p>
            <p className="text-2xl font-bold text-gray-900">{formatNumber(totalCount)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600 mb-1">Total Arrears</p>
            <p className="text-2xl font-bold text-orange-600">
              {formatCurrency(totalArrears)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <p className="text-sm text-gray-600 mb-1">Average Arrears</p>
            <p className="text-2xl font-bold text-gray-900">
              {totalCount > 0
                ? formatCurrency(totalArrears / totalCount)
                : "$0.00"}
            </p>
          </div>
        </div>

        {/* Loans Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : loans.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No unassigned loans found.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 cursor-pointer"
                          checked={isAllSelected && loans.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Loan #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client Name
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Late Days
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Arrears
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loans.map((loan) => (
                      <tr
                        key={loan._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 cursor-pointer"
                            checked={selectedLoans.includes(loan._id)}
                            onChange={() => handleLoanSelection(loan._id)}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleLoanClick(loan._id)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                          >
                            {loan.loanNumber}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {loan.client?.personal?.fullName || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                          {formatNumber(loan.lateDays)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                          {formatCurrency(
                            loan.due?.totalAllDue || loan.due?.totalDue || 0
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            Unassigned
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing page <span className="font-medium">{currentPage}</span> of{" "}
                        <span className="font-medium">{totalPages}</span>
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnassignedLoans;

